<template>
  <div
    v-loading="mainLoader"
    class="gs-personal-details-container"
  >
    <div class="personal-details">
      <h2>Organisation details</h2>
      <el-form
        id="submitForm"
        ref="submitForm"
        :model="form"
        :rules="rules"
        @submit.prevent.native="submitOrganisationForm"
        @validate="handleSubmitFormFieldInput"
      >
        <validation-errors
          v-if="validationErrors.length"
          :errors="validationErrors"
        ></validation-errors>
        <el-form-item
          prop="name"
          class="form-group"
        >
          <template slot="label">
            Organisation name
            <span
              class="sr-only"
              aria-live="polite"
              :hidden="!hasInlineValidationErrorFor('name')"
              v-text="getInlineValidationErrorFor('name')"
            ></span>
          </template>
          <el-input
            id="name"
            v-model="form.name"
            placeholder="Organisation name"
            maxlength="64"
            auto-complete="no"
            label="Please enter your organisation name in this field"
            aria-required="true"
            @blur="form.name = form.name.trim()"
          />
        </el-form-item>
        <el-form-item
          v-loading="removeLogoLoader"
          class="form-group"
          prop="image"
          element-loading-background="rgba(255, 255, 255, 0.8)"
        >
          <template slot="label">
            Organisation profile image
          </template>  
          <div
            v-if="organisationFile"
            class="uploaded-file d-flex"
          >
            <el-avatar
              shape="circle"
              :size="140"
              :src="avatarUrl"
              alt="Organisation Profile picture"
              class="step-1-avatar"
            ></el-avatar>

            <div class="flex-fill">
              <div class="upload-actions">
                <el-upload
                  ref="upload"
                  action=""
                  :multiple="false"
                  :drag="true"
                  list-type="picture-card"
                  class="selected-upload"
                  :auto-upload="false"
                  :on-change="onFileChange"
                >
                  <div slot="default">
                    <el-button class="replace-button">
                      Replace
                    </el-button>
                  </div>

                  <div
                    v-if="organisationFile"
                    slot="file"
                    slot-scope="{file}"
                  >
                  </div>
                </el-upload>
                <span
                  v-if="customAvatar"
                  class="remove-file-link"
                  @click="removeSelectedAvatar"
                >Remove</span>
              </div>
            </div>
          </div>

          <el-upload
            ref="upload"
            action=""
            :multiple="false"
            :drag="true"
            list-type="picture-card"
            class="initial-upload"
            :class="{ 'upload-show': organisationFile }"
            :auto-upload="false"
            :on-change="onFileChange"
          >
            <div slot="default">
              <div class="se-file-upload">
                <img
                  src="../../../../static/img/file-drag-drop.svg"
                  alt="Drag & drop files here"
                  class="drag-drop-img"
                />
                <span class="or-text se-body-s-screen-16 m-4">or</span>
                <el-button class="browse-button">
                  Browse files
                </el-button>
              </div>
            </div>

            <div
              v-if="organisationFile"
              slot="file"
            >
            </div>
          </el-upload>
        </el-form-item>
        <el-row v-if="avatarLoader">
          <el-col
            :xs="24"
            :sm="12"
            :md="12"
            :lg="12"
          >
            <el-progress
              :percentage="percentage"
              color="#0b3459"
            ></el-progress>
          </el-col>
        </el-row>
        <el-form-item
          prop="contact_number"
          class="form-group"
        >
          <template slot="label">
            Contact number
            <span
              class="sr-only"
              aria-live="polite"
              :hidden="!hasInlineValidationErrorFor('contact_number')"
              v-text="getInlineValidationErrorFor('contact_number')"
            ></span>
          </template>
          <el-input
            id="contact_number"
            v-model="form.contact_number"
            class="w-lg-75"
            maxlength="20"
            autocomplete="false"
            label="Please enter your organisation, contact number in this field"
            aria-required="true"
            @blur="form.contact_number = form.contact_number.trim()"
          />
        </el-form-item>
        <el-form-item
          prop="email"
          class="form-group"
        >
          <template slot="label">
            Organisation email address
            <span
              class="sr-only"
              aria-live="polite"
              :hidden="!hasInlineValidationErrorFor('email')"
              v-text="getInlineValidationErrorFor('email')"
            ></span>
          </template>
          <el-input
            id="email"
            v-model="form.email"
            type="email"
            autocomplete="false"
            label="Please enter your desired organisation email address in this field"
            aria-required="true"
            @blur="form.email = form.email.trim()"
          />
        </el-form-item>
        <el-form-item
          prop="website_address"
          class="form-group"
        >
          <template slot="label">
            Website address<br />
            <p class="el-form-item__info">Please ensure your website address begins with https://</p>
            <span
              class="sr-only"
              aria-live="polite"
              :hidden="!hasInlineValidationErrorFor('website_address')"
              v-text="getInlineValidationErrorFor('website_address')"
            ></span>
          </template>
          <el-input
            id="website_address"
            v-model="form.website_address"
            type="text"
            autocomplete="false"
            label="Please enter your desired website address in this field"
            aria-required="true"
            @blur="form.website_address = form.website_address.trim()"
          />
        </el-form-item>
        <address-selector
          :address="form.address"
          @update="($event)=> form.address = $event "
        />
        <div class="step-hr mb-4"></div>
        <div class="d-flex form-actions">
          <el-button
            class="btn-cancel"
            @click="
              $router.push({
                name: 'Member Profile',
                params: { id: user.id },
              })
            "
          >
            Cancel
          </el-button>
          <el-button
            type="primary"
            native-type="submit"
            class="btn-submit"
          >
            Save changes
          </el-button>
        </div>
      </el-form>
    </div>
    <cropper-upload
      v-if="cropperFile"
      :visible="showCropperDialog"
      :cropper-file="cropperFile"
      @upload="onCropperUpload"
      @close="onCropperDialogClose"
    ></cropper-upload>
  </div>
</template>

<script>
import store from '@/store'
import { mapState, mapGetters } from "vuex"
import gsApi from '@/services/gs.api'
import CropperUpload from '@/components/cropper-upload/cropper-upload'
import validationErrors from '@/components/partials/validation-errors'
import AddressSelector from '@/components/partials/address-selector'
import inlineValidationErrorsMixin from '@/mixins/inlineValidationErrorsMixin'
import { validateWebsiteAddress, validateEmail, validateContactNumber} from '@/helpers/validators'
export default {
  name: "OrganisationDetails",

  components: {
    validationErrors,
    'cropper-upload': CropperUpload,
    AddressSelector
  },
  mixins: [inlineValidationErrorsMixin],
  data() {
    return {
      percentage : 1,
      cropperFile: null,
      showCropperDialog: false,
      validationErrors: [],
      mainLoader: false,
      avatarLoader: false,
      removeLogoLoader:false,
      currentLogo: store.state.user?.organisation?.organisation_photo,
      customAvatar: store.state.user?.organisation?.has_custom_avatar, // the organisation has already a logo or default one
      form: {
        name: store.state.user?.organisation?.name ,
        contact_number: store.state.user?.organisation?.contact_number,
        organisation_photo: store.state.user?.organisation?.organisation_photo,
        website_address:store.state.user?.organisation?.website_address,
        email:store.state.user?.organisation?.email,
        address:store.state.user?.organisation.address ? store.state.user?.organisation.address : {'address_1' : '',
          'address_2' : '',
          'post_code' : '',
          'town' : '',
          'county' : ''}
      },
      rules: {
        name: [
          { required: true, message: 'Last name is required', trigger: 'blur' },
          { max: 64, message: 'Organisation name may not be greater than 64 characters', trigger: 'blur' },
        ],
        website_address: [
          { required: false, validator: validateWebsiteAddress },
        ],
        email: [
          { required: false, validator: validateEmail },
        ],
        contact_number: [
          { required: false, validator: validateContactNumber },
        ],
      },
      organisationFile:null,
      addressesLoading: false
    }
  },
  computed: {
    ...mapState(['user']),
    ...mapGetters(['isOrganisationOwner']),
    disableOrganisationInput(){
      return !!this.user?.expertProfile?.organisation
    },
    avatarUrl: function () {
      return this.organisationFile.url
    }
  },
  created(){
    if(!this.isOrganisationOwner)
    {
      this.$route.push('/feed')
    }
  },
  methods: {
    // Start of upload / cropper methods
    removeSelectedAvatar() {
      this.removeLogoLoader = true

      gsApi.member.organisation.removePicture()
        .then(response => {
          this.removeLogoLoader = false

          this.currentLogo = response.data.logo
          this.customAvatar = response.data.has_custom_logo

          store.commit('ORANISATION_LOGO_CHANGE', response.data)
          this.$alert(response.data.message, 'Success', { confirmButtonText: 'Close', showClose: false, customClass: 'notification-modal' });
        })
        .catch(() => {
          this.removeLogoLoader = false
          this.$alert('An unknown error occurred. Please try again later.', 'Error', { confirmButtonText: 'Close', showClose: false, customClass: 'notification-modal' });
        })
    },
    onFileChange(file) {
      if (this.validateAvatarUpload(file)) {
        this.cropperFile = file
        this.showCropperDialog = true
      }
    },
    onCropperUpload(croppedFileData) {
      this.onAvatarUpload(croppedFileData)
    },
    onAvatarUpload (croppedFileData) {

      this.organisationFile = croppedFileData
      this.form.organisation_photo = croppedFileData.file
      this.onCropperDialogClose()
    },
    onCropperDialogClose() {
      this.showCropperDialog = false
      this.cropperFile = null
    },
    validateAvatarUpload(file) {
      let type = (file.raw && file.raw.type) ? file.raw.type : file.type
      let size = (file.raw && file.raw.size) ? file.raw.size : file.size

      const isJPG = type === 'image/jpeg';
      const isPNG = type === 'image/png';
      const isLt6M = size / 1024 / 1024 < 10;

      if (!isJPG && !isPNG) {
        this.$alert('Avatar must be in a jpeg or png format.', 'Error', { confirmButtonText: 'Close', showClose: false, customClass: 'notification-modal' });
      } else if (!isLt6M) {
        this.$alert('Avatar size can not exceed 6MB.', 'Error', { confirmButtonText: 'Close', showClose: false, customClass: 'notification-modal' });
      }

      return (isJPG || isPNG) && isLt6M;
    },
    // End of upload / cropper methods
    submitOrganisationForm() {
      let self = this;
      this.$refs['submitForm'].validate((valid) => {
        if (valid) {
          const formData = new FormData()
          this.mainLoader = true
          formData.append('name', this.$options.filters.domPurify(this.form.name, {ALLOWED_TAGS: []}))
          formData.append('contact_number', this.$options.filters.domPurify(this.form.contact_number, {ALLOWED_TAGS: []}))
          formData.append('website_address', this.$options.filters.domPurify(this.form.website_address, {ALLOWED_TAGS: []}))
          formData.append('email', this.$options.filters.domPurify(this.form.email, {ALLOWED_TAGS: []}))
          formData.append('address_1', this.$options.filters.domPurify(this.form.address.address_1, {ALLOWED_TAGS: []}))
          formData.append('address_2', this.$options.filters.domPurify(this.form.address.address_2, {ALLOWED_TAGS: []}))
          formData.append('post_code', this.$options.filters.domPurify(this.form.address.post_code, {ALLOWED_TAGS: []}))
          formData.append('town', this.$options.filters.domPurify(this.form.address.town, {ALLOWED_TAGS: []}))
          formData.append('county', this.$options.filters.domPurify(this.form.address.county, {ALLOWED_TAGS: []}))
          formData.append('country_id', this.form.address.country_id ? this.form.address.country_id : this.form.address?.country?.id)

          if (this.form.organisation_photo) {

            formData.append("organisation_photo", this.form.organisation_photo)
          }

          gsApi.organisation.updateOrganisationInfo(formData)
            .then(response => {
              self.$store.commit('UPDATE_ORGANISATION_DETAILS', response.data);
              this.mainLoader = false;
              this.validationErrors = [];
              this.$alert('Success! your organisation details have been updated', 'Success', { confirmButtonText: 'Close', showClose: false, customClass: 'notification-modal' });
            })
            .catch(error => {
              this.validationErrors = [];
              this.mainLoader = false

              if (error.response && error.response.status == 422 && error.response.data) {
                let errors = error.response.data.errors;
                for (let [key, value] of Object.entries(errors)) {
                  let obj = {}
                  obj.id = key;
                  obj.message = value[0];
                  this.validationErrors.push(obj);
                }
                window.scrollTo({top: 0, behavior: 'smooth'});
              } else {
                this.$alert('There are errors in the form, please fix these errors and try again.', 'Error', { confirmButtonText: 'Close', showClose: false, customClass: 'notification-modal' });
              }
            })
        } else {
          this.validationErrors = [];
          this.$refs.submitForm.fields.forEach(field => {
            if(field.validateState == 'error') {
              let obj = {}
              obj.id = field.labelFor;
              obj.message = field.validateMessage;
              this.validationErrors.push(obj);
            }
          });
          this.$nextTick(() => {
            document.getElementById('formErrorBlock').scrollIntoView({ behavior: 'smooth', block: 'start' })
            let text = document.getElementById("submitForm").getElementsByClassName('el-form-item__error')[0].textContent
            this.$announcer.set(text)
          })
        }
      });
    },
    handleSubmitFormFieldInput () {
      this.handleSingleFormFieldInput('submitForm');
    },
  }
}
</script>
